import { oids } from "@/configs/Settings";

/**
 * Portal Clínico IPES (POC) embarcado via iframe.
 * A base fica só neste módulo para o legado não repetir a URL.
 */
export const IPES_EMBEDDED_PORTAL_BASE_URL =
  "https://portal.poc.ipes.tech/embedded-portal/";

/** Mesmo purposeofuse usado no Patient Summary do produto. */
export const IPES_PURPOSE_OF_USE = "Atendimento";

export interface IpesEmbeddedPortalParams {
  resourceId: string;
  subjectId: string;
  organizationId: string;
  token: string;
}

/**
 * Remove prefixo OID FHIR (`oids.cpf-` / `oids.cnpj-`) antes de extrair dígitos.
 * Assim um id `2.16...-12345678901` vira só o documento, não o OID concatenado.
 */
function stripKnownOidPrefix(value: string): string {
  const prefixes = [
    `${oids.cpf}-`,
    `${oids.cnpj}-`,
    `urn:oid:${oids.cpf}-`,
    `urn:oid:${oids.cnpj}-`,
  ];
  const afterSlash = value.includes("/")
    ? (value.split("/").pop() ?? value)
    : value;

  for (const prefix of prefixes) {
    if (afterSlash.startsWith(prefix)) {
      return afterSlash.slice(prefix.length);
    }
  }

  return afterSlash;
}

/**
 * CPF/CNPJ só com dígitos, sem máscara e sem OID.
 * Equivale a `useFormatter.removeSpecialChars` após tirar o prefixo FHIR.
 * Rejeita UUID/texto (Patient.id lógico) para não mandar lixo no portal.
 */
export function sanitizeIpesIdentifier(value?: unknown): string {
  if (typeof value !== "string" || !value) return "";
  const stripped = stripKnownOidPrefix(value.trim());
  if (/[a-zA-Z]/.test(stripped)) return "";
  return stripped.replace(/\D/g, "");
}

/**
 * Monta a URL do portal. Devolve null se faltar qualquer parâmetro obrigatório.
 * `URLSearchParams` já aplica encode no token (não usar Bearer).
 */
export function buildIpesEmbeddedPortalUrl(
  params: IpesEmbeddedPortalParams,
): string | null {
  const resourceId = sanitizeIpesIdentifier(params.resourceId);
  const subjectId = sanitizeIpesIdentifier(params.subjectId);
  const organizationId = sanitizeIpesIdentifier(params.organizationId);
  const token = 'cc5c6697-ccc4-42fd-81aa-32a5db6424b7';

  if (!resourceId || !subjectId || !organizationId || !token) {
    return null;
  }

  const url = new URL(IPES_EMBEDDED_PORTAL_BASE_URL);
  url.searchParams.set("resource-id", resourceId);
  url.searchParams.set("subject-id", subjectId);
  url.searchParams.set("organization-id", organizationId);
  url.searchParams.set("purposeofuse", IPES_PURPOSE_OF_USE);
  url.searchParams.set("token", token);
  return url.toString();
}
